import { JstsPolygon, JstsUtils } from 'Plugins/FloorPlan/Utils/JstsUtils';
import { LaizeStairsCalculatorResult, ProductType, StairsResult } from '../Laize';
import { CalculLaizeStairsParams } from '../LaizeCalc';
import { RoomType, StairsStepType } from 'Plugins/FloorPlan/Models/IRoom';
import { CoordPoint } from 'Plugins/FloorPlan/Utils/Types';
import { binPackingNFDHStairsUnpacked } from './BinPackingNFDH';
import { PointUtils } from 'Plugins/FloorPlan/Utils/PointUtils';

export interface SingleStairStep {
    id: string;
    staircaseId: string;
    staircaseIndex: number;
    staircaseName: string;
    staircaseFloor?: string;
    name: string;
    type: StairsStepType;
    marches: number; //Emmarchement
    giron: number; //Profondeur de marche
    contremarches?: number;
}

export interface Band {
    points: CoordPoint[];
    remainingBand: JstsPolygon;
}

export class LaizeStairs {
    public static calculLaizeStairs(params: CalculLaizeStairsParams): LaizeStairsCalculatorResult | undefined {
        if (params.laizeCalcProps.TYPE !== ProductType.Rouleau) {
            return undefined;
        }
        const bandWidthCm = params.laizeCalcProps.BAND_WIDTH;
        const bandLengthCm = params.laizeCalcProps.LONGUEUR_CM;
        const band: Band = {
            points: [
                { x: 0, y: 0 },
                { x: bandWidthCm, y: 0 },
                { x: bandWidthCm, y: -bandLengthCm },
                { x: 0, y: -bandLengthCm },
                { x: 0, y: 0 },
            ],
            remainingBand: JstsUtils.createJstsPolygon([
                { x: 0, y: 0 },
                { x: bandWidthCm, y: 0 },
                { x: bandWidthCm, y: -bandLengthCm },
                { x: 0, y: -bandLengthCm },
                { x: 0, y: 0 },
            ]),
        };

        const stairsByStaircaseThenStairsPack = params.rooms
            .filter((room) => room.type === RoomType.Stairs)
            .filter((staircase) => staircase.stairsSteps !== undefined)
            .map((staircase) => ({
                staircaseId: staircase.roomId!,
                staircaseIndex: staircase.staircaseIndex,
                staircaseName: staircase.name!,
                staircaseFloor: staircase.questionsBaseContext?.stairs_floor,
                stairs: staircase.stairsSteps!,
            }));

        const stairsUnpacked: Array<SingleStairStep> = [];

        for (const staircase of stairsByStaircaseThenStairsPack) {
            let stairIndex = 0;
            for (const stairPack of staircase.stairs) {
                for (let i = 0; i < stairPack.quantity; i++) {
                    stairsUnpacked.push({
                        ...stairPack,
                        id: `${staircase.staircaseId}-${stairIndex}`,
                        staircaseId: staircase.staircaseId,
                        staircaseName: staircase.staircaseName,
                        staircaseFloor: staircase.staircaseFloor,
                        staircaseIndex: staircase.staircaseIndex,
                    }); // Unpack each stairPack into individual stairs
                    stairIndex++
                }
            }
        }

        // Reorder stairs for optimal fit
        const { stairsResults, totalLength } = binPackingNFDHStairsUnpacked(
            stairsUnpacked,
            params.laizeCalcProps.LAIZE_MARGIN,
            params.rooms,
            band,
            params.laizeCalcProps,
            params.logName
        );

        return {
            band,
            stairs: stairsResults,
            laizeResult: totalLength / 100,
        };
    }

    public static createRectangleFromCursorPosition(
        currentCursorHeight: number,
        currentCursorWidth: number,
        width: number,
        length: number
    ): JstsPolygon {
        // Create rectangle with the bottom left point at the cursor position
        const rectanglePoints = [
            { x: currentCursorWidth, y: -currentCursorHeight },
            { x: currentCursorWidth + width, y: -currentCursorHeight },
            { x: currentCursorWidth + width, y: -currentCursorHeight - length },
            { x: currentCursorWidth, y: -currentCursorHeight - length },
            { x: currentCursorWidth, y: -currentCursorHeight }, // Close the polygon
        ];

        return JstsUtils.createJstsPolygon(rectanglePoints);
    }

    public static getBandConsumedLength(
        stairs: StairsResult[],
        staircaseId: string
    ): { bandId: string; bandConsumedLength: number }[] {
        const stairsOfStaircase = stairs.filter((s) => s.staircaseId === staircaseId);
        const bandsArray: Array<{ bandId: string; stairs: StairsResult[] }> = [];
        for (const stair of stairsOfStaircase) {
            let band = bandsArray.find((b) => b.bandId === stair.bandId);
            if (!band) {
                band = { bandId: stair.bandId, stairs: [] };
                bandsArray.push(band);
            }
            band.stairs.push(stair);
        }

        const bandsConsumedLength = bandsArray.map((band) => {
            const bandStairPoints = band.stairs.flatMap((stair) => stair.pointsWithMargin);
            const bbox = PointUtils.calculateBoundingBox({ points: bandStairPoints });
            const length = Math.abs(bbox!.yMax - bbox!.yMin);
            return { bandId: band.bandId, bandConsumedLength: length / 100 };
        });

        return bandsConsumedLength;
    }
}
